import React, { useState, useEffect, useRef } from "react";
import { PageContainer } from "../components/Container/PageContainer";
import { useTranslation } from "react-i18next";
import { createCollection } from "../utils/pureDataFetch";
import { useUser } from "../contexts/UserContext";
import UpdateCollectionModal from "../components/modal/UpdateCollectionModal";
import DataLoader from "../components/DataLoader";
import MyCollectionListSection from "../components/MyCollectionListSection";
import { useThreadsState } from "../contexts/ThreadsContext";
import { CollectionSummarySection } from "../template/account/CollectionSummarySection";
import { useIsMobile } from "../hooks/useIsMobile";
import { useCollectionInit } from "../hooks/useCollectionInit";
import { CollectionDebugPanel } from "../components/CollectionDebugPanel";
import { dbService } from "../firebaseInstance";

const MyPage = ({ firstThreadDate }) => {
  const { t } = useTranslation();
  const { profile } = useUser();
  const { lifetimeThreadsList, isLoading } = useThreadsState();
  const [modalOpen, setModalOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedThreads, setSelectedThreads] = useState([]);
  const [collectionList, setCollectionList] = useState([]);
  
  // 개선된 컬렉션 초기화 훅 사용
  const { 
    collections: myCollections, 
    isLoading: collectionsLoading, 
    error: collectionsError,
    initStatus,
    retry: retryCollections 
  } = useCollectionInit();
  
  const [sortedCollections, setSortedCollections] = useState([]);
  const [sortMetric, setSortMetric] = useState("views");
  const [aggregationType, setAggregationType] = useState("total");
  const isMobile = useIsMobile('xl');

  // 컬렉션 로딩 상태 디버깅
  useEffect(() => {
    const userId = profile?.id || localStorage.getItem("userID");
    const token = localStorage.getItem("accessToken");
    
    console.log("[MyPage] Initial state check:", {
      userId,
      hasToken: !!token,
      hasProfile: !!profile,
      profileId: profile?.id,
      collectionsCount: myCollections.length,
      isLoading: collectionsLoading,
      initStatus,
      error: collectionsError,
      dbService: !!window.firebase?.firestore
    });
  }, [profile, myCollections, collectionsLoading, initStatus, collectionsError]);

  // 임시 테스트: 직접 Firestore 호출
  useEffect(() => {
    const testDirectFirestore = async () => {
      const userId = profile?.id || localStorage.getItem("userID");
      if (!userId) return;
      
      console.log("🔥 [DIRECT TEST] Testing direct Firestore access for userId:", userId);
      
      try {
        const userRef = dbService.collection("user").doc(userId);
        const userDoc = await userRef.get();
        console.log("🔥 [DIRECT TEST] User doc exists:", userDoc.exists);
        
        if (userDoc.exists) {
          const userData = userDoc.data();
          console.log("🔥 [DIRECT TEST] User data:", userData);
          console.log("🔥 [DIRECT TEST] CollectionList:", userData.collectionList);
        }
      } catch (error) {
        console.error("🔥 [DIRECT TEST] Error:", error);
      }
    };
    
    testDirectFirestore();
  }, [profile?.id]);

  const handleThreadSelect = (threadId) => {
    setSelectedThreads((prev) =>
      prev.includes(threadId)
        ? prev.filter((id) => id !== threadId)
        : [...prev, threadId]
    );
  };

  useEffect(() => {
    setCollectionList(() => {
      // lifetimeThreadsList가 없으면 빈 배열 사용
      const threadsList = lifetimeThreadsList || [];
      return threadsList.filter((thread) =>
        selectedThreads.includes(thread.id)
      );
    });
  }, [selectedThreads, lifetimeThreadsList]);

  const handleRemoveThread = (threadId) => {
    setSelectedThreads((prev) => prev.filter((id) => id !== threadId));
    setCollectionList((prev) =>
      prev.filter((thread) => thread.id !== threadId)
    );
  };

  const handleCreateCollection = async (collectionData) => {
    try {
      const newCollection = await createCollection(collectionData);
      console.log("생성된 새 컬렉션:", newCollection);
      // 컬렉션 재로드 트리거
      retryCollections();
      setModalOpen(false);
    } catch (error) {
      console.error("컬렉션 생성 오류:", error);
    }
  };

  // 수정된 정렬 함수
  const handleSort = (collections, metric, aggregation) => {
    if (!collections.length) return [];

    return [...collections].sort((a, b) => {
      const getMetricValue = (collection) => {
        // metric 객체에서 직접 값을 가져옴
        const value = collection.metric?.[metric] || 0;

        if (aggregation === "average") {
          // threadList 배열의 길이로 나누어 평균 계산
          const threadCount = collection.threadList?.length || 1;
          return value / threadCount;
        }
        return value;
      };

      const valueA = getMetricValue(a);
      const valueB = getMetricValue(b);

      return valueB - valueA; // 내림차순 정렬
    });
  };

  // 정렬 실행
  useEffect(() => {
    const sorted = handleSort(myCollections, sortMetric, aggregationType);
    setSortedCollections(sorted);
  }, [myCollections, sortMetric, aggregationType]);

  // 정렬 기준 변경 핸들러
  const handleSortMetricChange = (value) => {
    setSortMetric(value);
  };

  // 집계 방식 변경 핸들러
  const handleAggregationTypeChange = (value) => {
    setAggregationType(value);
  };

  return (
    <PageContainer>
      <DataLoader msg={t("loading.lifetime")} isLoad={isLoading} />
      
      {/* 개발 환경에서만 디버그 패널 표시 */}
      {process.env.NODE_ENV === 'development' && (
        <CollectionDebugPanel userId={profile?.id || localStorage.getItem("userID")} />
      )}
      
      {/* 에러 상태 표시 */}
      {collectionsError && initStatus === 'error' && (
        <div style={{ 
          padding: '20px', 
          margin: '20px 0', 
          background: '#fee', 
          borderRadius: '8px',
          border: '1px solid #fcc'
        }}>
          <p style={{ color: '#c00', margin: 0 }}>
            컬렉션을 불러오는 중 오류가 발생했습니다: {collectionsError}
          </p>
          <button 
            onClick={retryCollections}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              background: '#c00',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            다시 시도
          </button>
        </div>
      )}
      
      {/* 컬렉션 요약 섹션 */}
      {profile && (
        <CollectionSummarySection
          profile={profile}
          collections={myCollections}
          postLoading={isLoading}
          firstThreadDate={firstThreadDate}
          setModalOpen={setModalOpen}
          isMobile={isMobile}
        />
      )}

      {/* 컬렉션 관리 섹션 */}
      {collectionsLoading ? (
        <DataLoader msg={t("loading.collections")} isLoad={true} />
      ) : (
        <MyCollectionListSection
          isMobile={isMobile}
          myCollections={sortedCollections}
          aggregationType={aggregationType}
          sortMetric={sortMetric}
          handleAggregationTypeChange={handleAggregationTypeChange}
          handleSortMetricChange={handleSortMetricChange}
        />
      )}
      <UpdateCollectionModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        title={title}
        setTitle={setTitle}
        description={description}
        setDescription={setDescription}
        threadsList={lifetimeThreadsList || []}
        selectedThreads={selectedThreads}
        handleThreadSelect={handleThreadSelect}
        isMobile={isMobile}
        onUpdateCollection={handleCreateCollection}
        collectionList={collectionList}
        handleRemoveThread={handleRemoveThread}
        isNew={true}
      />
    </PageContainer>
  );
};

export default MyPage;
